import axios from "axios";
import {errorNotify} from "../utils/app";
import {CSRF, DIRECT_MESSAGES_ACTIONS} from "../constants";


export const requestConversationsSuccess = (data) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.REQUEST_CONVOS,
        payload: { data }
    }
}

export const requestMessagesSuccess = (data=[], meta) => ({
    type: DIRECT_MESSAGES_ACTIONS.REQUEST_MESSAGES,
    payload: { data },
    meta,
});

export const requestMoreMessagesSuccess = (data=[], meta={}) => ({
    type: DIRECT_MESSAGES_ACTIONS.REQUEST_MORE_MESSAGES_SUCCESS,
    payload: { data },
    meta
});

export const addMessage = (data) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.ADD_DIRECT_MESSAGES,
        payload: { data }
    }
}

export const addLastActive = (data=null) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.ADD_LAST_ACTIVE,
        payload: { data }
    }
}

export const addConvo = (data) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.ADD_CONVO,
        payload: { data }
    }
}

export const editConvo  = (convoId, attributes) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.UPDATE_CONVO,
        payload: { convoId, attributes }
    }
}

export const moveToTopConvo  = (convoId) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.CONVO_TO_TOP,
        payload: { convoId }
    }
}

export const deleteConvo  = (convoId) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.DELETE_CONVO,
        payload: { convoId }
    }
}

export const editMessage  = (messageId, attributes) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.UPDATE_DIRECT_MESSAGE,
        payload: { messageId, attributes }
    }
}

export const deleteMessage  = (messageId) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.DELETE_DIRECT_MESSAGE,
        payload: { messageId }
    }
}


export const requestConversations = (options = {}) => {
    return (dispatch, getState) => {
        const state   = getState()
        const filters = state.direct_messages.filters
        const { bottom } = filters
        const prevConvos = state.direct_messages.conversations || []

        // 1. Build params from bottom filters (existing behavior)
        const params = []

        Object.keys(bottom).forEach((key) => {
            const value = bottom[key]
            if (value !== undefined && value !== null && value !== '') {
                params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            }
        })

        // 2. Add pagination params (new)
        const per = options.per || 10
        params.push(`per=${encodeURIComponent(per)}`)

        if (options.before) {
            params.push(`before=${encodeURIComponent(options.before)}`)
        }

        const queryString = params.join('&')
        const url = `/pages/direct_messages/conversations?${queryString}`

        try {
            return axios.get(url)
                .then(json => {
                    // backend now returns:
                    // { data: [...], unarchived_convos_count: ..., meta: { has_more, oldest_cursor } }
                    const { data, unarchived_convos_count, meta = {} } = json.data

                    const finalData = options.append
                        ? [...prevConvos, ...data] // append for "load more"
                        : data                     // replace for first page / filters change

                    // SAME actions as before → reducers untouched
                    dispatch(requestConversationsSuccess(finalData))
                    dispatch(requestMessagesSuccess())
                    dispatch(setUnarchivedConvosCount(unarchived_convos_count))
                    
                    // Also update dashboard counter when conversations are loaded/refreshed
                    // (e.g., after archiving/unarchiving)
                    dispatch({
                        type: DIRECT_MESSAGES_ACTIONS.REQUEST_MESSAGES_COUNTER_SUCCESS,
                        payload: { num: unarchived_convos_count }
                    })

                    const hasMore = meta.has_more ?? false
                    const cursor  = meta.oldest_cursor ?? null

                    return { hasMore, cursor }
                })
                .catch(error => {
                    errorNotify(error?.response?.data?.error || 'Error loading conversations')
                })
        } catch (e) {
            console.error("Error of requesting automations", e)
            return Promise.reject(e)
        }
    }
}

const buildBottomFilterQuery = (bottom) =>
    Object.keys(bottom).map(key => key + '=' + bottom[key]).join('&');

export const requestMessages = (senderId, pageId, opts = {}) => {
    return async (dispatch, getState) => {
        const filters = getState().direct_messages.filters;
        const { bottom } = filters;
        const filterBottomParams = buildBottomFilterQuery(bottom);
        const per = opts.per || 30;

        const url = `/pages/${pageId}/direct_messages/?id=${senderId}&${encodeURI(filterBottomParams)}&per=${per}`;

        try {
            const res = await axios.get(url);
            const payload = res?.data?.data || [];
            const meta = res?.data?.meta || {};
            dispatch(requestMessagesSuccess(payload, {
                hasMore: !!meta.has_more,
                oldestCursor: meta.oldest_cursor || null
            }));
            return { hasMore: !!meta.has_more, count: payload.length };
        } catch (error) {
            errorNotify(error?.response?.data?.error || 'Failed to load messages');
            return { hasMore: false, count: 0 };
        }
    };
};

export const requestMoreMessages = (senderId, pageId, { before, per = 30 } = {}) => {
    return async (dispatch, getState) => {
        if (!before) return { hasMore: false, count: 0 };

        const filters = getState().direct_messages.filters;
        const filterBottomParams = buildBottomFilterQuery(filters.bottom);

        const url = `/pages/${pageId}/direct_messages/?id=${senderId}&${encodeURI(filterBottomParams)}&per=${per}&before=${encodeURIComponent(before)}`;

        try {
            const res = await axios.get(url);
            const data = res?.data?.data || [];
            const meta = res?.data?.meta || {};

            dispatch(requestMoreMessagesSuccess(data, {
                hasMore: !!meta.has_more,
                oldestCursor: meta.oldest_cursor || null
            }));

            const hasMore = meta.has_more === true && data.length > 0;
            return { hasMore, count: data.length };
        } catch (e) {
            errorNotify(e?.response?.data?.error || 'Failed to load older messages');
            return { hasMore: false, count: 0 };
        }
    };
};


export const sendMessage = (messageId, pageId, data) => {
    return (dispatch, getState) => {
        if(messageId) {
            data.append("id", messageId)
            try {
                axios.post(`/pages/${pageId}/direct_messages`, data, {headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': CSRF}})
                    .then(json => {
                        dispatch(addMessage(json.data.data))
                        dispatch(moveToTopConvo(json.data.data.attributes.convo_id))
                    }).catch(error => {
                })
            } catch (e) {
                console.error("")
            }
        }
    }
}

export const changeMessageFilter = (top=false, name=null, value=null) => {
    return (dispatch) => {
        dispatch({
            type: DIRECT_MESSAGES_ACTIONS.CHANGE_DM_FILTER,
            payload: { top, name, value }
        })
    }
}

export const updateConversation = (messageId, pageId, paramsName) => {
    return (dispatch, getState) => {
        try {
            axios.put(`/pages/${pageId}/direct_messages/${messageId}`, {dm_conversation: {[paramsName]: true}}, {headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': CSRF}})
                .then(json => {
                    dispatch(requestConversations())
                    // dispatch(editConvo(json.data.data.attributes.convo_id, json.data.data.attributes))
                }).catch(error => console.error(error))
        } catch (e) {
            console.error("")
        }
    }
}

export const archiveAllConversations = (pageId) => {
    return dispatch => {
        try {
            axios.post('/direct_messages/archive_all', {pageId: pageId})
                .then(json => {
                    dispatch(requestConversations())
                }).catch(error => console.error(error))
        } catch (e) {
            console.error("")
        }
    }
}

export const setUnarchivedConvosCount = (num=1) => {
    return {
        type: DIRECT_MESSAGES_ACTIONS.SET_UNARCHIVED_CONVOS_COUNT,
        payload: { num }
    }
}

export const requestMessagesCounter = () => {
    return (dispatch, getState) => {
        const filters = getState().direct_messages.filters
        const { bottom } = filters
        const params = []

        Object.keys(bottom).forEach((key) => {
            const value = bottom[key]
            if (value !== undefined && value !== null && value !== '') {
                params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            }
        })

        const queryString = params.join('&')
        const url = `/pages/direct_messages/conversations?only_count=true&${queryString}`

        dispatch({ type: DIRECT_MESSAGES_ACTIONS.REQUEST_MESSAGES_COUNTER_STARTED })
        try {
            axios.get(url)
                .then(json => {
                    const {unarchived_convos_count} = json.data
                    dispatch({
                        type: DIRECT_MESSAGES_ACTIONS.REQUEST_MESSAGES_COUNTER_SUCCESS,
                        payload: { num: unarchived_convos_count }
                    })
                }).catch(error => {
                dispatch({
                    type: DIRECT_MESSAGES_ACTIONS.REQUEST_MESSAGES_COUNTER_FAILED,
                    payload: error
                })
            })
        } catch(e) {
            console.error("Error of requesting messages counter")
        }
    }
}